import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Container, Flex, Spinner, Text } from "@chakra-ui/react";
import { FiArrowRight } from "react-icons/fi";
import { calculateEventCalculator, getEventCalculatorOptions } from "../../services/eventCalculator";
import { itemPrimaryQuantity } from "./helpers";
import { QUESTION_STEPS, STEPS } from "./constants";
import { StepProgress } from "./StepProgress";
import { StepPeople } from "./steps/StepPeople";
import { StepEventType } from "./steps/StepEventType";
import { StepDuration } from "./steps/StepDuration";
import { StepDrinkCategories } from "./steps/StepDrinkCategories";
import { ResultsScreen } from "./ResultsScreen";

const HERO_CHECKLIST = ["4 perguntas rápidas", "Sem cadastro", "Pedido pronto no carrinho"];

export function EventCalculatorWizard({ variant = "page" }) {
  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState(null);

  const [stepIndex, setStepIndex] = useState(0);

  const [answers, setAnswers] = useState({
    people: null,
    peopleMode: "preset",
    event_type: null,
    duration: null,
    duration_days: null,
    drink_categories: [],
  });

  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState(null);
  const [itemQuantities, setItemQuantities] = useState({});
  const [navbarHeight, setNavbarHeight] = useState(0);
  const cardRef = useRef(null);
  const skipNextScrollRef = useRef(true);

  // The fixed Navbar's height varies by breakpoint, scroll state, and its own
  // conditional rows (promo ticker, category links row), so we measure it at
  // runtime instead of guessing pixel values that drift whenever Navbar changes.
  useLayoutEffect(() => {
    if (variant !== "hero") return;
    const el = document.getElementById("boozen-navbar");
    if (!el) return;
    const update = () => setNavbarHeight(el.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [variant]);

  const currentStep = STEPS[stepIndex];

  async function fetchOptions() {
    try {
      const response = await getEventCalculatorOptions();
      setOptions(response.data);
    } catch (error) {
      setOptionsError(
        "Não foi possível carregar a calculadora agora. Tente novamente em instantes."
      );
    }
  }

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Correction C4 (briefing v5): which drink categories are selectable
  // depends on live catalog stock, so /options is refetched every time the
  // user reaches Passo 4 instead of trusting the mount-time snapshot — stock
  // sold out (or replenished) mid-session shouldn't offer/hide stale choices.
  useEffect(() => {
    if (currentStep === "drink_categories") fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Re-anchors the scroll position to the top of the card on every step
  // change. Without this, going back from a tall step (e.g. the results
  // screen) to a short one (e.g. Passo 1) leaves the window scrolled at the
  // same absolute Y it was at before — which, now that the content above it
  // collapsed, can land well past the card entirely. Skips the very first
  // render so landing on the page doesn't yank the viewport down to the
  // card on mount.
  useEffect(() => {
    if (skipNextScrollRef.current) {
      skipNextScrollRef.current = false;
      return;
    }
    const el = cardRef.current;
    if (!el) return;
    const offset = (variant === "hero" ? navbarHeight : 0) + 16;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  // Seeds itemQuantities from the freshly-calculated suggestions. Keyed on
  // calcResult so it only (re)runs on a new response and never clobbers
  // in-progress edits the user made to the current one.
  useEffect(() => {
    if (!calcResult) return;
    const seeded = {};
    calcResult.results.forEach((result) => {
      result.suggested_items.forEach((item) => {
        seeded[`${result.key}:${item.batch_id}`] = itemPrimaryQuantity(item);
      });
    });
    setItemQuantities(seeded);
  }, [calcResult]);

  const isQuestionStep = QUESTION_STEPS.includes(currentStep);

  const selectedDurationOption = options?.durations?.find((d) => d.key === answers.duration);

  function canAdvance() {
    if (currentStep === "people") return !!answers.people && answers.people > 0;
    if (currentStep === "event_type") return !!answers.event_type;
    if (currentStep === "duration") {
      if (!answers.duration) return false;
      if (selectedDurationOption?.requires_duration_days) {
        return (
          !!answers.duration_days &&
          answers.duration_days >= (selectedDurationOption.min_duration_days || 1)
        );
      }
      return true;
    }
    if (currentStep === "drink_categories") return answers.drink_categories.length > 0;
    return true;
  }

  async function runCalculate() {
    setCalcLoading(true);
    setCalcError(null);
    try {
      const payload = {
        people: answers.people,
        event_type: answers.event_type,
        duration: answers.duration,
        drink_categories: answers.drink_categories,
      };
      if (selectedDurationOption?.requires_duration_days) {
        payload.duration_days = answers.duration_days;
      }
      const response = await calculateEventCalculator(payload);
      setCalcResult(response.data);
      setStepIndex(STEPS.indexOf("results"));
    } catch (error) {
      setCalcError(
        error?.response?.data?.error ||
          "Não foi possível calcular sua recomendação. Tente novamente."
      );
    } finally {
      setCalcLoading(false);
    }
  }

  function handleNext() {
    if (currentStep === "drink_categories") {
      runCalculate();
      return;
    }
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  }

  function handleBack() {
    setStepIndex((i) => Math.max(i - 1, 0));
  }

  function handleAdjust() {
    setStepIndex(STEPS.indexOf("drink_categories"));
  }

  const cardContent = (
    <>
      {optionsError && (
        <Text color="red.500" fontSize="13px">
          {optionsError}
        </Text>
      )}

      {!optionsError && !options && (
        <Box display="flex" justifyContent="center" py="40px">
          <Spinner color="#004a33" />
        </Box>
      )}

      {options && (
        <>
          {isQuestionStep && (
            <StepProgress
              stepIndex={QUESTION_STEPS.indexOf(currentStep)}
              totalSteps={QUESTION_STEPS.length}
            />
          )}

          {currentStep === "people" && (
            <StepPeople presets={options.people_presets} answers={answers} setAnswers={setAnswers} />
          )}
          {currentStep === "event_type" && (
            <StepEventType eventTypes={options.event_types} answers={answers} setAnswers={setAnswers} />
          )}
          {currentStep === "duration" && (
            <StepDuration durations={options.durations} answers={answers} setAnswers={setAnswers} />
          )}
          {currentStep === "drink_categories" && (
            <StepDrinkCategories
              categories={options.drink_categories}
              answers={answers}
              setAnswers={setAnswers}
            />
          )}
          {currentStep === "results" && calcResult && (
            <ResultsScreen
              calcResult={calcResult}
              itemQuantities={itemQuantities}
              setItemQuantities={setItemQuantities}
              onAdjust={handleAdjust}
            />
          )}

          {calcError && currentStep === "drink_categories" && (
            <Text color="red.500" fontSize="13px" mt="10px">
              {calcError}
            </Text>
          )}

          {isQuestionStep && (
            <Box display="flex" justifyContent="space-between" mt="16px" gap="10px">
              <Button
                variant="ghost"
                color="#004a33"
                fontWeight="700"
                onClick={handleBack}
                visibility={stepIndex === 0 ? "hidden" : "visible"}
              >
                Voltar
              </Button>
              <Button
                bg="#fc8f04"
                color="#00301f"
                fontWeight="900"
                borderRadius="14px"
                px="22px"
                h="44px"
                rightIcon={<FiArrowRight size={16} />}
                isDisabled={!canAdvance()}
                isLoading={calcLoading && currentStep === "drink_categories"}
                onClick={handleNext}
                _hover={{ opacity: 0.92 }}
              >
                {currentStep === "drink_categories" ? "Ver minha recomendação" : "Continuar"}
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );

  if (variant === "hero") {
    return (
      <Box
        bg="linear-gradient(135deg, #03301f 0%, #004a33 55%, #04593d 100%)"
        position="relative"
        overflow="hidden"
        pt={`${navbarHeight}px`}
      >
        <Container maxW="1320px" px={{ base: "16px", md: "32px" }}>
          <Box
            display="grid"
            gridTemplateColumns={{ base: "1fr", lg: "1.05fr 0.95fr" }}
            gap={{ base: "32px", lg: "56px" }}
            alignItems="center"
            py={{ base: "36px", lg: "64px" }}
          >
            {/* Marketing copy */}
            <Box textAlign={{ base: "left", lg: "left" }}>
              <Text
                fontSize="12px"
                fontWeight="800"
                color="#fc8f04"
                textTransform="uppercase"
                letterSpacing="0.16em"
                mb="14px"
              >
                Calculadora Boozen
              </Text>
              <Text
                fontFamily="'Anton', sans-serif"
                textTransform="uppercase"
                fontSize={{ base: "36px", sm: "44px", lg: "48px", xl: "56px" }}
                lineHeight="0.98"
                letterSpacing="0.01em"
                color="white"
                mb="18px"
              >
                Chega de
                <br />
                calcular sozinho
                <br />
                <Box as="span" color="#fc8f04">
                  A Boozen resolve
                </Box>
              </Text>
              <Text
                fontSize={{ base: "14px", md: "15px" }}
                color="rgba(255,255,255,.72)"
                fontWeight="500"
                maxW="440px"
                mx="0"
                mb="22px"
              >
                Conte como vai ser o seu evento. A gente calcula a quantidade certa de cada bebida e já
                monta o pedido pra você.
              </Text>
              <Flex wrap="no-wrap" gap={{ base: "10px 8px", md: "10px 24px" }} justify={{ base: "flex-start", lg: "flex-start" }}>
                {HERO_CHECKLIST.map((label) => (
                  <Flex key={label} align="center" gap="6px">
                    <Box color="#fc8f04" fontWeight="900" fontSize={{ base: "10px", md: "14px" }} lineHeight="1">
                      ✓
                    </Box>
                    <Text fontSize={{ base: "13px", md: "14px" }} fontWeight="700" color="white">
                      {label}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Box>

            {/* Wizard card */}
            <Box position="relative" maxW={{ base: "480px", lg: "100%" }} mx={{ base: "auto", lg: "0" }} w="100%">
              <Box
                position="absolute"
                inset="16px -16px -16px 16px"
                bg="#fc8f04"
                borderRadius="24px"
                display={{ base: "none", sm: "block" }}
              />
              <Box
                ref={cardRef}
                position="relative"
                bg="white"
                borderRadius="20px"
                boxShadow="0 20px 60px rgba(0,0,0,.28)"
                p={{ base: "16px", md: "24px" }}
              >
                {cardContent}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="transparent">
      <Container maxW="680px" px={{ base: "16px", md: "24px" }}>
        <Box
          ref={cardRef}
          bg="white"
          borderRadius="18px"
          border="1px solid rgba(0,74,51,0.08)"
          boxShadow="0 4px 24px rgba(0,74,51,.06)"
          p={{ base: "14px", md: "20px" }}
        >
          {cardContent}
        </Box>
      </Container>
    </Box>
  );
}
